<template>
  <div class="n-input" :class="{ 'has-error': errorMessage }">
    <label :for="inputId" :class="{ 'sr-only': hideLabel }">{{ label }}</label>
    <div class="select-wrapper">
      <select
        class="control"
        :id="inputId"
        :name="name"
        :disabled="disabled"
        v-model="selectedValue"
        @change="handleSelectChange"
      >
        <slot></slot>
        <option
          v-for="(option, index) in options"
          :key="getOptionKey(option, index)"
          :value="getOptionValue(option)"
          :disabled="isOptionDisabled(option)"
        >
          {{ getOptionLabel(option) }}
        </option>
      </select>
    </div>
    <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
  </div>
</template>

<script lang="ts" setup>
import { watch, ref } from 'vue';
import { PropType } from 'vue';
import { useIdGenerator } from '@/shared/designsystem/useIdGenerator';

defineOptions({
  name: 'n-select',
});

const props = defineProps({
  id: { type: String, default: '' },
  label: { type: String, default: '' },
  name: { type: String, default: '' },
  modelValue: { type: [Object, String, Number, Boolean] as PropType<any>, default: '' },
  options: { type: Array as PropType<any[]>, required: true },
  labelProp: { type: String, default: 'label' },
  valueProp: { type: String, default: 'value' },
  disabledProp: { type: String, default: 'disabled' },
  errorMessage: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  hideLabel: { type: Boolean, default: false },
});

const { inputId } = useIdGenerator(props.id);

const selectedValue = ref<any>(null);

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
}>();

// Sync internal selectedValue with modelValue
watch(
  () => props.modelValue,
  (newValue: any) => {
    selectedValue.value = getOptionValue(newValue);
  },
  { immediate: true },
);

// Extract label from an option
function getOptionLabel(option: any): string {
  if (option === null || option === undefined) return '';
  if (typeof option === 'object' && props.labelProp in option) {
    return option[props.labelProp];
  }
  return String(option); // Fallback for primitives
}

// Extract value from an option
function getOptionValue(option: any): any {
  if (option === null || option === undefined) return null;
  if (typeof option === 'object' && props.valueProp in option) {
    return option[props.valueProp];
  }
  return option; // Use the whole option if primitive or no valueProp
}

// Check if an option is disabled
function isOptionDisabled(option: any): boolean {
  if (typeof option === 'object' && props.disabledProp in option) {
    return !!option[props.disabledProp];
  }
  return false;
}

// Generate a unique key for v-for
function getOptionKey(option: any, index: number): string {
  const value = getOptionValue(option);
  if (value !== null && value !== undefined) {
    return String(value);
  }
  return `${index}`;
}

function handleSelectChange() {
  const selectedOption = props.options.find((opt: any) => {
    const optValue = getOptionValue(opt);
    return optValue === selectedValue.value || JSON.stringify(optValue) === JSON.stringify(selectedValue.value);
  });
  emit('update:modelValue', selectedOption ?? null);
}
</script>

<style lang="scss" scoped>
@use '../../../styles/accessibility' as *;
@use '../../../styles/colours' as *;
@use '../../../styles/input' as *;

.select-wrapper {
  position: relative;
  cursor: pointer;
  &::after {
    border: 3px solid transparent;
    border-radius: 2px;
    border-right: 0;
    border-top: 0;
    content: ' ';
    display: block;
    height: 7px;
    margin-top: -0.4375em;
    pointer-events: none;
    position: absolute;
    top: 50%;
    -webkit-transform: rotate(-45deg);
    transform: rotate(-45deg);
    -webkit-transform-origin: center;
    transform-origin: center;
    width: 7px;
    border-color: #3273dc;
    right: 0.625em;
    z-index: 4;
    box-sizing: content-box;
  }
  &:hover {
    &::after {
      border-color: #363636;
    }
  }
  select.control {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    padding-right: 2.5em;
    cursor: pointer;
    max-width: 100%;
    font-size: 1rem;
    &:hover {
      border-color: #b5b5b5;
    }
    &:disabled {
      /*For FireFox*/
      -webkit-appearance: none;
      /*For Chrome*/
      -moz-appearance: none;
      &:hover {
        border-color: colourScheme('disabled');
      }
    }
  }
  select::-ms-expand {
    display: none;
  }
}
</style>
